import { Component, ElementRef, HostBinding, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, Validators, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FieldExpression, NodeExpression,
	phrase, toString as luceneToString, OperatorExpression, term, LuceneExpression } from 'lucene';
import { Observable, Subscription, map, of,
	switchMap, startWith, debounceTime, distinctUntilChanged, shareReplay, tap, forkJoin } from 'rxjs';
import moment from 'moment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import {
	MatCheckboxChange,
	MatCheckboxModule
} from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { HttpEventType } from '@angular/common/http';
import { FileDescriptor, FileUploadModule } from '@copo/file-upload-new';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AllQualityIssue } from '../../models/all-quality-issue';
import {
	QualityIssueSample,
	QualityIssueType
} from '../../models/all-quality-issue-constants';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import {
	DEFECT_DETAILED_OPTIONS_KEYS,
	DEFECT_DETAILED_OPTIONS,
	UNIT_OPTIONS,
	UNIT_OPTIONS_KEYS,
	CURRENCY_OPTIONS,
	CURRENCY_OPTIONS_KEYS,
	WEIGHT_OPTIONS
} from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { LocationService } from '../../../shared/services/location.service';
import { UserService } from '../../../shared/services/user.service';
import { BlobService } from '../../../shared/utils/blob.service';
import { HashService } from '../../../shared/services/hash.service';
import {
	SnackbarSaveDraftComponent
} from '../utils/snackbar-save-draft.component';
import { TranslationService } from '../../../shared/services/translation.service';
import { AllQualityIssueAttachment } from '../../models/all-quality-issue-attachment';
import { GROSS } from '../../../shared/models/settings';
import { AllQualityIssueBatch, getQualityIssueBatchQuantity } from '../../models/all-quality-issue-batch';
import { QualityIssueBatchService } from '../../services/quality-issue-batch-service';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { padSupplierOrderReference, unpadSoldto } from '../../../shared/utils/padding';
import { CustomLocation } from '../../../shared/services/location.custom';
import { formatNumber } from '../../../shared/utils/number.format';
import { StepClickedEvent, StepStatus, StepperComponent } from '../../../shared/components/stepper/stepper.component';
import { sumBatchQuantitiesRoundValue } from '../../utils/batches.utils';
import { DialogUnrecognizedBatchComponent, RESPONSE_CONFIRM }
	from '../utils/dialog-unrecognized-batch/dialog-unrecognized-batch.component';
import { AllQualityIssueService } from '../../services/all-quality-issue.service';
import { ClaimBatch, NON_PRIME_ORDER_REASONS, NORMAL_SUPPLIER_ORDER_TYPES } from '../../models/claim-batch';
import { QualityIssueService } from '../../services/quality-issue.service';
import { ClaimConfirmationDialogComponent } from '../utils/claim-confirmation-dialog.component';
import { ClaimStatus } from '../../models/quality-issue';
import { GlobalErrorHandler } from '../../../shared/utils/error-handler';
import { SoldToRelationshipGroup } from '../../../shared/models/service';
import { DATE_CRITERION_TYPE, DATE_FORMAT_PATTERN } from '../../../shared/models/filter-formats';
import { OrderService } from '../../../order/services/order.service';
import { DialogBatchNotInvoicedComponent } from '../utils/dialog-batch-not-invoiced.component';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import { FieldOfDocument, DOCUMENTS_SUB_ENTITY } from '../../../commercial-documents/models/document';
import { DialogBatchFromSameInvoiceComponent } from '../utils/dialog-batch-from-same-invoice.component';
import { DimensionPipe } from '../../../shared/pipes/format-dimension.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { CopoDatepickerInputDirective } from '../../../shared/directives/date-picker.directive';
import { SlideButtonComponent } from '../../../shared/components/slide-button/slide-button.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';

interface ErrorObject {
	batchRef: boolean;
	quantity: boolean;
	defect: boolean;
	sample: boolean;
	comment: boolean;
	attachmentsMandatory: boolean;
	attachmentsInError: boolean;
	attachmentsUploading: boolean;
	otherCostAmountMandatory: boolean;
	receiptDateMandatory: boolean;
	attachmentFileError: boolean;
}

const defectOptionsValues = DEFECT_DETAILED_OPTIONS_KEYS.map(key => ({ key, value: DEFECT_DETAILED_OPTIONS[key] }));
const unitBatchOptionsValues = UNIT_OPTIONS_KEYS.map(key => ({ key, value: UNIT_OPTIONS[key] }));
const unitAmountOptionsValues = CURRENCY_OPTIONS_KEYS.map(key => ({ key, value: CURRENCY_OPTIONS[key] }));

interface BatchReference {
	reference: string;
	qty: number;
	modificationDate: Date;
}

const invoiceRequiredFields: readonly FieldOfDocument[] = [
	'id',
	'type',
	'subtype',
	'date',
	'name',
	'documentLines',
	'millCode'
];

@Component({
	selector: 'copo-quality-issue-entry-form',
	templateUrl: './quality-issue-entry-form.component.html',
	styleUrl: './quality-issue-entry-form.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		FormsModule,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		StepperComponent,

		MatRadioModule,

		MatFormFieldModule,

		MatInputModule,
		ReactiveFormsModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,
		FileUploadModule,
		TextFieldModule,

		MatCheckboxModule,
		SlideButtonComponent,
		CopoDatepickerInputDirective,
		MatDatepickerModule,
		LoadingSpinnerComponent,
		VirtualScrollerDirective,
		RouterLink,
		UpperCasePipe,
		NumberPipe,
		DimensionPipe,

		MatTooltipModule
	]
})
export class QualityIssueEntryFormComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@ViewChild('mainBatchDiv', { read: ElementRef }) mainBatchDiv!: ElementRef<HTMLElement>;
	@ViewChild('batchesDiv', { read: ElementRef }) batchesDiv!: ElementRef<HTMLElement>;
	@ViewChild('defectDiv', { read: ElementRef }) defectDiv!: ElementRef<HTMLElement>;
	@ViewChild('sampleDiv', { read: ElementRef }) sampleDiv!: ElementRef<HTMLElement>;
	@ViewChild('attachmentDiv', { read: ElementRef }) attachmentDiv!: ElementRef<HTMLElement>;
	@ViewChild('commentDiv', { read: ElementRef }) commentDiv!: ElementRef<HTMLElement>;
	@ViewChild('currencyDiv', { read: ElementRef }) currencyDiv!: ElementRef<HTMLElement>;
	@ViewChild('scroller', { read: ScrollerDirective }) scroller!: ScrollerDirective;
	@ViewChild('otherCostAmountDiv', { read: ElementRef }) otherCostAmountDiv!: ElementRef<HTMLElement>;

	readonly defectOptions = DEFECT_DETAILED_OPTIONS;
	readonly defectOptionsValues = defectOptionsValues;
	readonly unitBatchOptionsValues = unitBatchOptionsValues;
	readonly unitAmountOptionsValues = unitAmountOptionsValues;
	readonly unitBatchOptions = UNIT_OPTIONS;
	readonly unitBatchOptionsKeys = UNIT_OPTIONS_KEYS;
	readonly unitAmountOptions = CURRENCY_OPTIONS;

	forcedQuantity = '';
	forcedCurrency = '';

	claim: AllQualityIssue;

	user: User;
	formatType: string = DATE_CRITERION_TYPE;
	currentDate = moment().format(DATE_FORMAT_PATTERN);
	notInvoiceBatchRefArray: string[] = [];

	/** error management for mandatory */
	onErrorMandatory: ErrorObject = {
		batchRef: false,
		quantity: false,
		defect: false,
		sample: false,
		comment: false,
		attachmentsMandatory: false,
		attachmentsInError: false,
		attachmentsUploading: false,
		otherCostAmountMandatory: false,
		receiptDateMandatory: false,
		attachmentFileError: false
	};

	batchesFormArray: FormArray<FormGroup<{
		fullQuantity: FormControl<boolean>;
		claimedQuantity: FormControl<number | null>;
		receiptDate: FormControl<Date | null>;
	}>>;

	batchesFormArraySubscriptions: Subscription[] = [];
	referenceFormControl: FormControl<typeof this.claim.reference>;
	haveAdditionalCostFormControl: FormControl<boolean>;
	claimTypeFormControl: FormControl<'ZL' | 'ZR'>;
	materialCostsFormControl: FormControl<number | null>;
	defectFormControl: FormControl<typeof this.claim.defectDetailedRef>;
	sendSampleFormControl: FormControl<'yes' | 'no'>;
	contactTypeFormControl: FormControl<typeof this.claim.sampleTr>;
	commentFormControl: FormControl<typeof this.claim.shortDescription>;
	otherCostAmountFormControl: FormControl<typeof this.claim.otherCostAmount | null>;
	searchTextControl: FormControl<string>;
	contactInfoFormControl: FormControl<typeof this.claim.sampleComment>;

	submitting = false;
	searchTextSubscription: Subscription;
	unrecognizedBatchSubscription?: Subscription;
	searchValue!: string;
	contactInformation = '';
	mainClaimBatch: ClaimBatch | undefined;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	displayingList = false;
	count: number | undefined = undefined;
	/** can select upto 5 batches excluding main-batch */
	readonly batchLimit = 5;
	selectedBatches = Array.from<AllQualityIssueBatch | null>({ length: this.batchLimit })
		.fill(null, 0, this.batchLimit);

	readonly UNDERSCORE = UNDERSCORE;
	soldToRelationshipGrps: Observable<SoldToRelationshipGroup[]>;
	claimDataSubscription: Subscription | undefined;
	claimedDialogSubscription: Subscription | undefined;
	isNonPrimeOrder = false;
	isBatchesLoading = true;
	togglePos = true;

	readonly steps;

	private readonly stateId: number;
	private readonly subscription: Subscription = new Subscription();
	private readonly batchDataSub?: Subscription;
	private invoiceBatchListObs: Observable<string[]> | Observable<undefined> | undefined;

	constructor(
		private readonly claimService: AllQualityIssueService,
		private readonly locationService: LocationService,
		private readonly blobService: BlobService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly hashService: HashService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly documentService: DocumentService,

		private readonly snackBar: MatSnackBar,
		private readonly batchService: QualityIssueBatchService,

		private readonly dialog: MatDialog,
		private readonly claimedService: QualityIssueService,
		private readonly errorHandler: GlobalErrorHandler,
		private readonly orderService: OrderService,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('claim.first.batch.selection'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('claim.quality.issue.information'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('claim.quality.issue.summary'),
				status: StepStatus.DISABLED,
			}
		];
		const res = <{ claim: AllQualityIssue }> this.route.snapshot.data;
		this.claim = res['claim'];

		this.claim.quantityUnitTr = <keyof typeof UNIT_OPTIONS> this.claim.quantityUnitTr.toLowerCase();
		this.claim.amountUnitTr = <keyof typeof CURRENCY_OPTIONS> this.claim.amountUnitTr.toLowerCase();
		this.stateId = CustomLocation.GET_CURRENT_STATE_ID();
		const searchState = <{ searchValue: string } | undefined>CustomLocation.GET_STATE('searchState', this.stateId);
		this.searchValue = !searchState ? '' : searchState.searchValue;
		this.searchTextControl = new FormControl(this.searchValue, { nonNullable: true });
		this.initializeSelectedBatches();
		this.claim.sendSample = false;

		this.batchesFormArray = new FormArray(this.claim.batches.map(
			batch => this.createBatchFormGroup(batch)
		));
		if (this.claim.batches[0].reference === '') {
			this.batchesFormArray.controls[0].controls.claimedQuantity.setValue(null);
		}
		const totalBatchQty = sumBatchQuantitiesRoundValue(this.claim.batches);
		this.claim.quantityValue = totalBatchQty;
		this.referenceFormControl = new FormControl(this.claim.reference, { nonNullable: true });
		this.haveAdditionalCostFormControl = new FormControl(this.claim.haveAdditionalCosts, { nonNullable: true });
		this.claimTypeFormControl = new FormControl<'ZL' | 'ZR'>(this.claim.claimType ?? 'ZL', { nonNullable: true });
		const materialCostsInitValue = this.claim.amountValue > 0 ? this.claim.amountValue : null;
		this.materialCostsFormControl = new FormControl<number | null>(materialCostsInitValue);

		this.defectFormControl = new FormControl(
			this.claim.defectDetailedRef,
			{ nonNullable: true, validators: [ctrl => this.onErrorMandatory.defect ? Validators.required(ctrl) : null] }
		);
		this.sendSampleFormControl = new FormControl(
			this.claim.sampleTr !== 'not' ? 'yes' : 'no',
			{ nonNullable: true, validators: [Validators.required] }
		);
		this.contactTypeFormControl = new FormControl(
			this.claim.sampleTr,
			{ nonNullable: true, validators: [ctrl => this.onErrorMandatory.sample ? Validators.required(ctrl) : null] }
		);
		this.otherCostAmountFormControl = new FormControl(
			this.claim.otherCostAmount === 0 ? null : this.claim.otherCostAmount,
			{
				validators: [ctrl => this.onErrorMandatory.otherCostAmountMandatory ? Validators.required(ctrl) : null]
			}
		);

		this.commentFormControl = new FormControl(
			this.claim.shortDescription,
			{
				nonNullable: true,
				validators: [ctrl => this.onErrorMandatory.comment ? Validators.required(ctrl) : null]
			}
		);
		this.contactInfoFormControl = new FormControl(this.claim.sampleComment, { nonNullable: true });

		this.user = this.userService.user;

		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			startWith(this.searchTextControl.value),
			debounceTime(500),
			distinctUntilChanged()
		).subscribe((value: string) => {
			this.searchValue = value;
		});

		const [{ reference, quantityValue }] = this.claim.batches;

		this.soldToRelationshipGrps = this.userService.getSoldToRelationshipGroupsTransac('QUAPP')
			.pipe(
				shareReplay({ bufferSize: 1, refCount: false })
			);

		if (!reference) {
			this.evtUnrecognizedBatch('');
			return;
		}

		this.batchDataSub = this.soldToRelationshipGrps
			.pipe(
				// search reference/quantity
				switchMap(transacGrps => {
					if (transacGrps.length === 0) {
						return of(undefined);
					}
					const soldTos = transacGrps.map(soldtoList => soldtoList.soldTo);
					const shipTos = Array.from(new Set(transacGrps.flatMap(v => v.shipTos)));
					if (soldTos.length === 0 && shipTos.length === 0) {
						return of(undefined);
					}
					return this.getBatchData(soldTos, shipTos, reference, quantityValue);
				}),
				// search reference if not found
				switchMap(batchDataSoldToTransacs => {
					if (!batchDataSoldToTransacs) {
						return of(undefined);
					}
					const { batchData, soldToTransacs, shipToTransacs } = batchDataSoldToTransacs;
					if (batchData.results.length === 0) {
						return this.getBatchData(soldToTransacs, shipToTransacs, reference);
					}
					return of(batchDataSoldToTransacs);
				}),
				switchMap(batchOrderData => {
					if (!batchOrderData) {
						return of(undefined);
					}
					const { batchData, soldToTransacs, shipToTransacs } = batchOrderData;
					if (batchData.results.length === 0) {
						this.evtUnrecognizedBatch(reference);
						return of(undefined);
					}
					const [batch] = batchData.results;
					if (!NORMAL_SUPPLIER_ORDER_TYPES.includes(batch.supplierOrderType)) {
						return of(<const>{ batchData, soldToTransacs, shipToTransacs, orderData: undefined });
					}
					const supplierOrderNumberField: FieldExpression = {
						field: 'supplierOrderNumber',
						term: batch.supplierOrderNumber,
						quoted: false
					};

					const supplierOrderItemNumberField: FieldExpression = {
						field: 'supplierOrderItemNumber',
						term: batch.supplierOrderItemNumber,
						quoted: false
					};

					const orderNode = {
						left: supplierOrderNumberField,
						operator: 'AND',
						right: supplierOrderItemNumberField,
						parenthesized: false
					};
					return this.orderService.getOrders(
						[
							'customerOrderNumber',
							'customerOrderItemNumber',
							'orderReason',
							'orderReasonDescription'
						],
						luceneToString(orderNode),
						0,
						1,
						new OrderBy('customerOrderNumber', OrderByDirection.ASC),
						false
					).pipe(
						map(result => <const>{ batchData, soldToTransacs, shipToTransacs, orderData: result.results })
					);
				})
			)
			.subscribe({
				next: batchDataSoldToTransacs => {
					if (batchDataSoldToTransacs) {
						const { batchData } = batchDataSoldToTransacs;
						const orderData = batchDataSoldToTransacs.orderData ?? undefined;
						if (batchData.results.length > 0) {
							this.mainClaimBatch = batchData.results[0];
							this.isNonPrimeOrder = orderData && orderData.length > 0
								? NON_PRIME_ORDER_REASONS.includes(orderData[0].orderReason)
								: false;
							this.isBatchesLoading = false;
							this.invoiceBatchListObs =
								this.mainClaimBatch.currencyInvoiced === 'PLN'
									? this.getInvoicesWithBatchList(this.mainClaimBatch).pipe(
										tap(invoicesWithBatchList => {
											if (invoicesWithBatchList.results.length > 0) {
												this.dialog.open<DialogBatchFromSameInvoiceComponent>(
													DialogBatchFromSameInvoiceComponent,
													{ data: invoicesWithBatchList.results.map(inv => inv.name),
														panelClass: 'copo-claim-dialog',
														disableClose: true }
												);
											}
										}),
										map(r => r.results.flatMap(batch => batch.documentLines
											.map(item => item.batchNumber))),
										shareReplay({ bufferSize: 1, refCount: false })
									) : of(undefined);
						} else if (batchData.results.length === 0 && this.claim.batches.length !== 0) {
							this.searchValue = this.claim.batches[0].reference;
							this.evtUnrecognizedBatch(this.claim.batches[0].reference);
						}
					}
				},
				error: (err: unknown) => {
					console.error('error getting batch data', err);
					this.errorHandler.handleError(err);
				}
			});
	}

	get files(): FileDescriptor[] {
		return this.claim.attachments.map(attachment => ({
			localFile: attachment.localFile,
			error: Boolean(attachment.error),
			progress: attachment.progress,
			type: attachment.type,
			name: attachment.name,
			url: attachment.localUrl ?? this.getAttachmentUrl(this.claim.steeluserId, attachment),
			data: attachment,
		}));
	}

	get fileErrors(): string[] {
		return [
			...this.onErrorMandatory.attachmentsMandatory
				? [this.translationService.getTR('claim.attachment.mandatory')] : [],
			...this.onErrorMandatory.attachmentsInError
				? [this.translationService.getTR('claim.attachment.error')] : [],
			...this.onErrorMandatory.attachmentsUploading
				? [this.translationService.getTR('claim.attachment.uploading.error')] : [],
			...this.onErrorMandatory.attachmentFileError
				? [this.translationService.getTR('claim.attachment.filename.error')] : []
		];
	}

	get displaySampleComment() {
		return this.claim.sampleTr === 'other';
	}

	get disabled() {
		return this.user.isClaimStrictlyReader() || !this.claim.isModifiable;
	}

	get dataId() {
		const [mainBatch] = this.claim.batches;
		return `claim-${ mainBatch.reference }-${ mainBatch.quantityValue }${ this.searchValue }
		-${ this.togglePos }`;
	}

	get mainBatch() {
		return this.claim.batches[0];
	}

	getInvoicesWithBatchList(mainClaimBatch: ClaimBatch) {
		return this.documentService.getDocuments(
			invoiceRequiredFields,
			this.constructInvoiceDocumentFilter(
				mainClaimBatch.supplierOrderNumber,
				mainClaimBatch.supplierOrderItemNumber,
				mainClaimBatch.batchReference
			),
			0,
			100,
			new OrderBy(SORT_BY, SORT_WAY),
			false
		);
	}

	createBatchFormGroup(batch: AllQualityIssueBatch) {
		const { value, unit } = getQualityIssueBatchQuantity(batch);
		const fullQuantityValue = batch.claimedQuantityValue === value;
		const claimedQuantity = new FormControl(
			batch.claimedQuantityValue === 0 ? null : batch.claimedQuantityValue,
			{
				validators: [
					ctrl => Validators.required(ctrl),
					...this.claim.claimType === 'ZR' ? [Validators.min(1)] : [],
					...this.claim.claimType !== 'ZR' ? [Validators.max(value)] : []
				]
			}
		);
		const sub = claimedQuantity.valueChanges.subscribe(val => {
			batch.claimedQuantityValue = val !== null ? Number(val.toFixed(3)) : 0;
			batch.claimedQuantityUnitTr = unit;
			const newTotalBatchQty = sumBatchQuantitiesRoundValue(this.claim.batches);
			this.claim.quantityValue = newTotalBatchQty;
		});
		const receiptDate = new FormControl(
			batch.receiptDate !== '' ? new Date(batch.receiptDate) : null,
			{
				validators: [ctrl => this.claim.claimType !== 'ZR' ? Validators.required(ctrl) : null]
			}
		);
		const sub1 = receiptDate.valueChanges.subscribe(val => {
			batch.receiptDate = val !== null ? new Date(
				moment(val)
					.format('YYYY-MM-DD')
			)
				.toISOString() : '';
		});
		this.batchesFormArraySubscriptions.push(sub, sub1);
		if (fullQuantityValue && this.claim.claimType !== 'ZR') {
			claimedQuantity.disable();
		}
		return new FormGroup({
			fullQuantity: new FormControl(fullQuantityValue, { nonNullable: true }),
			claimedQuantity,
			receiptDate
		});
	}

	onBatchQuantityChange(batch: AllQualityIssueBatch, claimedQuantity: FormControl) {
		const val = <number | null> claimedQuantity.value;
		batch.claimedQuantityValue = val !== null ? Number(val.toFixed(3)) : 0;
		if (this.claim.claimType !== 'ZR' && batch.claimedQuantityValue > getQualityIssueBatchQuantity(batch).value) {
			this.onErrorMandatory.quantity = true;
			return;
		}
		if (batch.claimedQuantityValue === 0) {
			claimedQuantity.setValue(null);
		} else if (val !== batch.claimedQuantityValue) {
			claimedQuantity.setValue(batch.claimedQuantityValue);
		}
		const newTotalBatchQty = sumBatchQuantitiesRoundValue(this.claim.batches);
		this.claim.quantityValue = newTotalBatchQty;
	}

	getBatchData(soldToTransacs: string[], shipToTransacs: string[], ref: string, qty?: number) {
		let allNode: FieldExpression | NodeExpression = {
			field: 'batchReference',
			term: phrase.escape(ref),
			quoted: true
		};
		if (qty) {
			const quantity1Field: FieldExpression = {
				field: 'qtyBatchGross',
				term: String(qty),
				quoted: false
			};
			const quantity2Field: FieldExpression = {
				field: 'qtyBatchGross',
				term: String(qty / 1000),
				quoted: false
			};
			const quantity3Field: FieldExpression = {
				field: 'qtyBatchNet',
				term: String(qty),
				quoted: false
			};
			const quantity4Field: FieldExpression = {
				field: 'qtyBatchNet',
				term: String(qty / 1000),
				quoted: false
			};
			const quantity5Field: FieldExpression = {
				field: 'qtyInvoiced',
				term: String(qty),
				quoted: false
			};
			const quantity6Field: FieldExpression = {
				field: 'qtyInvoiced',
				term: String(qty / 1000),
				quoted: false
			};

			const qty1Node: NodeExpression = {
				left: quantity1Field,
				operator: 'OR',
				parenthesized: false,
				right: quantity2Field
			};

			const qty2Node: NodeExpression = {
				left: qty1Node,
				operator: 'OR',
				parenthesized: false,
				right: quantity3Field
			};

			const qty3Node: NodeExpression = {
				left: qty2Node,
				operator: 'OR',
				parenthesized: false,
				right: quantity4Field
			};

			const qty4Node: NodeExpression = {
				left: qty3Node,
				operator: 'OR',
				parenthesized: false,
				right: quantity5Field
			};

			const qty5Node: NodeExpression = {
				left: qty4Node,
				operator: 'OR',
				parenthesized: true,
				right: quantity6Field
			};

			allNode = {
				left: allNode,
				operator: 'AND',
				parenthesized: false,
				right: qty5Node
			};
		}

		const transacNode = this.claimService.createTransacSoldtoAndShiptoFilter(soldToTransacs, shipToTransacs);

		allNode = {
			left: allNode,
			operator: 'AND',
			parenthesized: false,
			right: transacNode
		};

		return this.batchService.getBatches(
			[
				'id',
				'batchReference',
				'supplierOrderNumber',
				'supplierOrderItemNumber',
				'millOrderNumber',
				'millOrderItemNumber',
				'soldTo',
				'soldToDescription',
				'shipTo',
				'qtyInvoiced',
				'weightUnitInvoiced',
				'currencyInvoiced',
				'sfcC',
				'thicknessDimension',
				'widthDimension',
				'lengthDimension',
				'qtyBatchGross',
				'qtyBatchNet',
				'steelgradeDescription',
				'cddWeek',
				'millDescription',
				'customerOrderNumber',
				'customerOrderItemNumber',
				'supplierOrderType',
				'batchInvoiced',
				'invoiceDate',
			],
			luceneToString(allNode),
			1,
			10,
			new OrderBy('cdd', OrderByDirection.DESC)
		)
			.pipe(
				map(batchData => <const>{ batchData, soldToTransacs, shipToTransacs })

			);
	}

	unpadSoldto(soldto: string) {
		return unpadSoldto(soldto);
	}

	typeChanged(type: QualityIssueType) {
		this.claim.type = type;
		if (type !== 'warning') {
			if (!this.claim.claimType) {
				this.claim.claimType = 'ZL';
				this.claimTypeFormControl.setValue('ZL');
			}
			return;
		}
		this.claim.claimType = null;
		this.claim.amountValue = 0;
		this.materialCostsFormControl.reset(null);
		this.claim.otherCostAmount = 0;
		this.otherCostAmountFormControl.reset(null);
	}

	claimTypeChanged(type: 'ZL' | 'ZR') {
		this.claim.claimType = type;
		this.updateReceiptDateValidation();
		if (type === 'ZL') {
			this.claim.amountValue = 0;
			this.materialCostsFormControl.reset(null);
			for (const [index, formGroup] of this.batchesFormArray.controls.entries()) {
				const { value } = getQualityIssueBatchQuantity(this.claim.batches[index]);
				formGroup.controls.claimedQuantity.setValidators([
					ctrl => Validators.required(ctrl),
					Validators.max(value)
				]);
				formGroup.controls.claimedQuantity.updateValueAndValidity();
				this.updateImpactedQuantityState(index);
			}
		} else {
			// ZR: full-quantity is not shown; enable controls and drop the max validator
			for (const formGroup of this.batchesFormArray.controls) {
				formGroup.controls.claimedQuantity.setValidators([
					ctrl => Validators.required(ctrl),
					Validators.min(1)
				]);
				formGroup.controls.claimedQuantity.enable();
				formGroup.controls.claimedQuantity.updateValueAndValidity();
			}
		}
	}

	materialCostsChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.claim.amountValue = target.value ? Number(Number.parseFloat(target.value).toFixed(2)) : 0;
		if (this.claim.amountValue === 0) {
			this.materialCostsFormControl.setValue(null);
		} else if (this.materialCostsFormControl.value !== this.claim.amountValue) {
			this.materialCostsFormControl.setValue(this.claim.amountValue);
		}
	}

	referenceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.claim.reference = target.value;
	}

	visitChanged(event: MatRadioChange) {
		this.claim.wantVisit = event.value === 'yes';
	}

	otherCostAmountChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.claim.otherCostAmount = target.value ? Number(Number.parseFloat(target.value).toFixed(2)) : 0;
		if (this.claim.otherCostAmount === 0) {
			this.otherCostAmountFormControl.setValue(null);
		} else if (this.otherCostAmountFormControl.value !== this.claim.otherCostAmount) {
			this.otherCostAmountFormControl.setValue(this.claim.otherCostAmount);
		}
	}

	additionalCostChanged(checked: boolean) {
		if (!checked) {
			this.claim.otherCostAmount = 0;
			this.otherCostAmountFormControl.reset(null);
		}
	}

	commentChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.claim.shortDescription = target.value.trim();
		if (this.commentFormControl.value !== this.claim.shortDescription) {
			this.commentFormControl.setValue(this.claim.shortDescription);
		}
	}

	sampleCommentChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.claim.sampleComment = target.value;
	}

	defectTypeChanged(event: MatSelectChange) {
		this.claim.defectDetailedRef = <keyof typeof DEFECT_DETAILED_OPTIONS> event.value;
		this.sendSampleFormControl.setValue('no');
		this.contactTypeFormControl.setValue('not');
		this.contactInfoFormControl.setValue('');
	}

	sendingSampleChanged() {
		this.claim.sampleTr = 'not';
		this.contactTypeFormControl.setValue('not');
		this.contactInfoFormControl.setValue('');
	}

	contactTypeChanged(event: MatSelectChange) {
		this.claim.sampleTr = <QualityIssueSample> event.value;
		this.contactInfoFormControl.setValue('');
	}

	addAttachments(events: FileList) {
		const files = Array.from(events);

		const { claim } = this;
		const { attachments } = claim;

		for (const file of files) {
			const localUrl = URL.createObjectURL(file);
			const attachment = new AllQualityIssueAttachment();
			attachment.name = file.name;
			attachment.size = file.size;
			attachment.type = file.type;
			attachment.localUrl = localUrl;
			attachment.localFile = file;
			attachment.creationDate = moment().toISOString();
			attachment.creationBy = `${ this.user.firstname } ${ this.user.lastname }`;
			attachments.push(attachment);
			for (let i = attachments.length - 1; i >= 0; i--) {
				const currentAttachment = attachments[i];
				if (currentAttachment !== attachment && currentAttachment.localFile === file) {
					attachments.splice(i, 1);
					currentAttachment.uploadSub?.unsubscribe();
					currentAttachment.uploadSub = undefined;
				}
			}
			this.hashService.readSHA1(file).then(hash => {
				attachment.hash = hash;
				for (let j = attachments.length - 1; j >= 0; j--) {
					const currentAttachment = attachments[j];
					if (currentAttachment !== attachment && currentAttachment.hash === hash) {
						attachments.splice(j, 1);
						currentAttachment.uploadSub?.unsubscribe();
						currentAttachment.uploadSub = undefined;
					}
				}
				this.checkAttachmentErrors(attachments);
				attachment.uploadSub = this.claimedService.uploadAttachment(file, hash).subscribe({
					next: event => {
						switch (event.type) {
						case HttpEventType.Sent:
							attachment.progress = undefined;
							break;
						case HttpEventType.ResponseHeader:
							attachment.progress = 0;
							break;
						case HttpEventType.UploadProgress:
							if (event.total) {
								attachment.progress = Math.round((event.loaded / event.total) * 100);
							}
							break;
						case HttpEventType.Response:
							if (event.body) {
								attachment.progress = 100;
								attachment.chronicleId = event.body.chronicleId;
								attachment.repository = event.body.repository;
								attachment.localFile = undefined;
								// we still need local url temporary because we need id for download
								if (attachment.localUrl && claim.id) {
									URL.revokeObjectURL(attachment.localUrl);
									attachment.localUrl = undefined;
								}
								this.checkAttachmentErrors(attachments);
							}
							break;
						// case HttpEventType.DownloadProgress:
						// case HttpEventType.User:
						default:
							break;
						}
					},
					complete: () => {
						attachment.uploadSub = undefined;
					},
					error: (err: unknown) => {
						attachment.error = true;
						if (!this.onErrorMandatory.attachmentsMandatory
							&& attachments.some(a => a.name.length > 80)) {
							this.onErrorMandatory.attachmentFileError = true;
						} else {
							this.onErrorMandatory.attachmentsInError = true;
						}
						this.checkAttachmentErrors(attachments);
						console.error('error uploading file', err);
					}
				});
			}).catch((err: unknown) => {
				attachment.error = true;
				if (!this.onErrorMandatory.attachmentsMandatory
					&& attachments.some(a => a.name.length > 80)) {
					this.onErrorMandatory.attachmentFileError = true;
				} else {
					this.onErrorMandatory.attachmentsInError = true;
				}
				this.checkAttachmentErrors(attachments);
				console.error('error calculating hash', err);
			});
		}
		this.checkAttachmentErrors(attachments);
	}

	checkAttachmentErrors(attachments: AllQualityIssueAttachment[]) {
		if (this.onErrorMandatory.attachmentsMandatory) {
			this.onErrorMandatory.attachmentsMandatory = attachments.length === 0;
		}
		if (this.onErrorMandatory.attachmentsUploading) {
			this.onErrorMandatory.attachmentsUploading =
				attachments.some(attach => !attach.error && attach.localFile);
		}
		if (this.onErrorMandatory.attachmentsInError) {
			this.onErrorMandatory.attachmentsInError =
				attachments.some(attach => attach.error);
		}
		if (!this.onErrorMandatory.attachmentsMandatory && this.onErrorMandatory.attachmentFileError) {
			this.onErrorMandatory.attachmentFileError = attachments.some(a => {
				const dotIndex = a.name.lastIndexOf('.');
				const nameWithoutExtension = dotIndex === -1 ? a.name : a.name.slice(0, dotIndex);
				return nameWithoutExtension.length > 80;
			});
		}
	}

	getAttachmentUrl(id: number, attachment: AllQualityIssueAttachment) {
		return attachment.localUrl
			|| this.claimService.getDownloadFile(id, attachment.hash, attachment.name);
	}

	async showAttachment(event: MouseEvent, attachment: AllQualityIssueAttachment) {
		const { href } = <HTMLAnchorElement> event.currentTarget;
		// ie11 blob url opening
		if (!href.startsWith('blob:') || !('msSaveOrOpenBlob' in navigator)) {
			return;
		}
		event.preventDefault();
		const blob = await this.blobService.getBlob(href);
		navigator.msSaveOrOpenBlob(blob, attachment.name);
	}

	removeAttachment(file: FileDescriptor) {
		const { attachments } = this.claim;
		for (let j = attachments.length - 1; j >= 0; j--) {
			const currentAttachment = attachments[j];
			if (currentAttachment === file.data) {
				attachments.splice(j, 1);
				currentAttachment.uploadSub?.unsubscribe();
				currentAttachment.uploadSub = undefined;
			}
		}
		this.checkAttachmentErrors(attachments);
	}

	resetOnError() {
		this.onErrorMandatory.batchRef = false;
		this.onErrorMandatory.quantity = false;
		this.onErrorMandatory.defect = false;
		this.onErrorMandatory.sample = false;
		this.onErrorMandatory.comment = false;
		this.onErrorMandatory.attachmentsMandatory = false;
		this.onErrorMandatory.receiptDateMandatory = false;
	}

	checkAttachedFilesErrors(event: Event, saveAsDraft: boolean) {
		let checkError = true;

		if (!saveAsDraft && this.claim.type !== 'warning' && this.claim.attachments.length === 0) {
			this.onErrorForm(this.attachmentDiv, 'attachmentsMandatory', event);
			return false;
		}

		if (this.claim.attachments.some(attachment => attachment.error)) {
			this.onErrorForm(this.attachmentDiv, 'attachmentsInError', event);
			checkError = false;
		}
		if (this.claim.attachments.some(attachment => !attachment.error && attachment.localFile)) {
			this.onErrorForm(this.attachmentDiv, 'attachmentsUploading', event);
			checkError = false;
		}
		return checkError;
	}

	prepareClaim() {
		this.resetOnError();
		const now = moment().toISOString();
		const userName = `${ this.user.firstname } ${ this.user.lastname }`;

		if (!this.claim.creationDate) {
			this.claim.creationDate = now;
			this.claim.modifDate = now;
			this.claim.creationBy = userName;
			this.claim.modifBy = userName;
		}
		if (this.claim.steeluserId) {
			this.claim.modifDate = now;
			this.claim.modifBy = userName;
		}
		for (const batch of this.claim.batches) {
			if (!batch.creationDate) {
				batch.creationDate = now;
				batch.creationBy = userName;
			}
			batch.modifDate = now;
			batch.modifBy = userName;
		}
	}

	async saveAsDraft(event: Event) {
		event.preventDefault();
		if (!this.checkAttachedFilesErrors(event, true)) {
			return;
		}
		this.prepareClaim();
		this.submitting = true;
		try {
			try {
				await this.claimService.saveIssue(this.claim);
			} catch (err) {
				console.error(err);
				this.snackBar.openFromComponent(
					SnackbarSaveDraftComponent,
					{
						duration: 5000,
						panelClass: ['copo-claim-draft-snackbar'],
						data: true
					}
				);
				return;
			}
			this.claimService.claim = new AllQualityIssue();
		} finally {
			this.submitting = false;
		}
		await this.router.navigate(['/quality-issues/drafts'], { replaceUrl: true });
		this.snackBar.openFromComponent(
			SnackbarSaveDraftComponent,
			{
				duration: 5000,
				panelClass: ['copo-claim-draft-snackbar'],
				data: false
			}
		);
	}

	onErrorForm(elem: ElementRef<HTMLElement> | undefined, onError: keyof ErrorObject, event: Event) {
		this.scrollIntoViewIfPresent(elem);
		this.onErrorMandatory[onError] = true;
		event.preventDefault();
	}

	goToSummary(event: Event) {
		// Update claim status to be ready to be pushed
		event.preventDefault();
		this.resetOnError();
		// check mandatory fields
		// we can't push if any mandatory are not filled

		if (this.claim.batches[0].reference === '') {
			this.onErrorForm(this.mainBatchDiv, 'batchRef', event);
			return;
		}

		// Defect
		if (this.claim.defectDetailedRef === '') {
			this.onErrorForm(this.defectDiv, 'defect', event);
			this.defectFormControl.markAsTouched();
			this.defectFormControl.updateValueAndValidity();
			return;
		}
		// Contact type (only if defect selected, forced to not)
		if (this.claim.sampleTr === 'not' && this.sendSampleFormControl.value === 'yes') {
			this.onErrorForm(this.sampleDiv, 'sample', event);
			this.contactTypeFormControl.markAsTouched();
			this.contactTypeFormControl.updateValueAndValidity();
			return;
		}

		// Attachment
		if (!this.checkAttachedFilesErrors(event, false)) {
			return;
		}
		// Comment
		if (this.claim.shortDescription === '') {
			this.onErrorForm(this.commentDiv, 'comment', event);
			this.commentFormControl.markAsTouched();
			this.commentFormControl.updateValueAndValidity();
			return;
		}
		// Amount claimed for other cost
		if (this.claim.otherCostAmount < 1 && this.haveAdditionalCostFormControl.value) {
			this.onErrorForm(this.otherCostAmountDiv, 'otherCostAmountMandatory', event);
			this.otherCostAmountFormControl.markAsTouched();
			this.otherCostAmountFormControl.updateValueAndValidity();
			return;
		}
		for (const formGroup of this.batchesFormArray.controls) {
			const formControl = formGroup.controls.claimedQuantity;
			const receiptDateFormControl = formGroup.controls.receiptDate;
			const invalidImpactedQuantity = formControl.value === null
				|| formControl.value <= 0
				|| (this.claim.claimType === 'ZR' && formControl.value < 1);
			if (invalidImpactedQuantity) {
				this.onErrorForm(this.batchesDiv, 'quantity', event);
				formControl.markAsTouched();
				formControl.updateValueAndValidity();
				return;
			}
			if (this.isNonPrimeOrder && this.claim.claimType !== 'ZR' && receiptDateFormControl.value === null) {
				this.onErrorForm(this.batchesDiv, 'receiptDateMandatory', event);
				receiptDateFormControl.markAsTouched();
				receiptDateFormControl.updateValueAndValidity();
				return;
			}
		}

		if (this.claim.claimType !== 'ZR' && this.claim.warnForQuantityGtBatchQty()) {
			this.onErrorForm(this.batchesDiv, 'quantity', event);
			return;
		}

		const [mainBatch] = this.claim.batches;

		if (this.claim.warnQuantity()) {
			this.forcedQuantity = `${
				this.claim.quantityValue
			} ${
				this.unitBatchOptions[this.claim.quantityUnitTr].label
			}`;
			this.claim.quantityValue = 0;
			this.claim.setQuantityUnitTr(mainBatch.receivedInvoiceQuantityUnitTr || 'to');
			this.scrollIntoViewIfPresent(this.batchesDiv);
			return;
		}

		if (this.claim.warnQuantityForTons()) {
			this.scrollIntoViewIfPresent(this.batchesDiv);
			return;
		}

		// check if all slected batches are invoiced, if not display popup
		if (this.notInvoiceBatchRefArray.length > 0) {
			this.dialog.open<DialogBatchNotInvoicedComponent>(
				DialogBatchNotInvoicedComponent,
				{ data: this.notInvoiceBatchRefArray,
					panelClass: 'copo-claim-dialog',
					disableClose: true }
			);
			return;
		}

		// claimed Batches Confirmation Dialog
		this.submitting = true;
		this.claimDataSubscription = forkJoin(
			this.claim.batches.map(
				batch => this.claimedService.getIssues(
					undefined,
					this.claimedService.getAllIssuesbyBatchReferenceFilter(
						batch.reference
					),
				).pipe(
					map(result => result.results)
				)
			)
		)
			.subscribe(
				async allData => {
					const data = allData.flat();
					const batchReferenceArray: BatchReference[] = [];

					for (const result of data) {
						for (const batch of result.batches) {
							if (
								this.claim.batches.some(
									claimBatchData =>
										claimBatchData.reference === batch.batchReference
										&& claimBatchData.quantityValue === batch.qty
								)
								&& result.status !== ClaimStatus.CANCELLED
								&& !batchReferenceArray.some(
									batchReference =>
										batchReference.reference === batch.batchReference
										&& batchReference.qty === batch.qty
										&& moment(result.modificationDate)
											.isBefore(moment(batchReference.modificationDate))
								)
							) {
								batchReferenceArray.some((res, index) => {
									if (res.reference === batch.batchReference) {
										batchReferenceArray.splice(index, 1);
									}
									return 0;
								});

								batchReferenceArray.push({
									reference: batch.batchReference,
									qty: batch.qty,
									modificationDate: moment(result.modificationDate).toDate()
								});
							}
						}
					}

					if (batchReferenceArray.length > 0) {
						this.claimedDialog(batchReferenceArray);
					} else {
						await this.goToSummaryScreen();
					}
				}
			);
	}

	async goToSummaryScreen() {
		// Always sync FormControl value back to model before preparing and submitting claim
		this.claim.claimType = this.claimTypeFormControl.value;
		this.prepareClaim();
		this.submitting = true;
		this.claimService.claim = this.claim;
		await this.router.navigate(
			['../quality-issue-confirmation'],
			{ replaceUrl: true, relativeTo: this.route }
		);
	}

	claimedDialog(references: BatchReference[]) {
		const sub = this.dialog.open<ClaimConfirmationDialogComponent>(
			ClaimConfirmationDialogComponent,
			{	data: references,
				panelClass: 'copo-claim-dialog',
				disableClose: true }
		);

		this.claimedDialogSubscription = sub.afterClosed().subscribe(async result => {
			const confirm = Boolean(result);
			if (confirm) {
				await this.goToSummaryScreen();
			} else {
				this.submitting = false;
			}
		});
	}

	reset() {
		this.referenceFormControl.reset(this.claim.reference);
		this.defectFormControl.reset(this.claim.defectDetailedRef);
		this.contactTypeFormControl.reset(this.claim.sampleTr);
		this.commentFormControl.reset(this.claim.shortDescription);
		this.otherCostAmountFormControl.reset(this.claim.otherCostAmount === 0 ? null : this.claim.otherCostAmount);

		this.resetOnError();

		this.forcedCurrency = '';
		this.forcedQuantity = '';
		this.scroller.setScrollTop(0);
	}

	goBack() {
		this.locationService.go(-1);
	}

	ngOnDestroy() {
		if (this.claimDataSubscription !== undefined) {
			this.claimDataSubscription.unsubscribe();
		}
		if (this.claimedDialogSubscription !== undefined) {
			this.claimedDialogSubscription.unsubscribe();
		}
		this.subscription.unsubscribe();
		if (this.batchDataSub) {
			this.batchDataSub.unsubscribe();
		}
		for (const sub of this.batchesFormArraySubscriptions) {
			sub.unsubscribe();
		}
		this.searchTextSubscription.unsubscribe();
		CustomLocation.SET_STATE(
			'searchState',
			this.stateId,
			{
				searchValue: this.searchTextControl.value
			}
		);
		this.unrecognizedBatchSubscription?.unsubscribe();
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<ClaimBatch[]>) => void) {
		const [{ reference }] = this.claim.batches;
		if (!reference || !this.invoiceBatchListObs) {
			provideData(of([]));
			return;
		}
		provideData(
			forkJoin([
				this.soldToRelationshipGrps,
				this.invoiceBatchListObs
			])
				.pipe(
					// search reference/quantity
					switchMap(([transacGrps, listOfRelatedBatches]) => {
						if (transacGrps.length === 0) {
							return of([]);
						}

						const soldTos = transacGrps.map(soldtoList => soldtoList.soldTo);
						const shipTos = Array.from(new Set(transacGrps.flatMap(v => v.shipTos)));
						if (soldTos.length === 0 && shipTos.length === 0) {
							return of([]);
						}

						if (listOfRelatedBatches && listOfRelatedBatches.length === 0) {
							return of([]);
						}

						const [mainBatch] = this.claim.batches;

						const supplierOrderNumberNode: FieldExpression | NodeExpression = {
							field: 'supplierOrderNumber',
							term: phrase.escape(mainBatch.receivedOrderSupplierReference),
							quoted: true
						};
						const supplierOrderItemNumberNode: FieldExpression = {
							field: 'supplierOrderItemNumber',
							term: phrase.escape(mainBatch.receivedOrderSupplierItem),
							quoted: true
						};

						const quantityUnitNode: FieldExpression = {
							field: 'weightUnitInvoiced',
							term: phrase.escape(mainBatch.receivedInvoiceQuantityUnitTr),
							quoted: false
						};

						const batchReferenceNode: FieldExpression = {
							field: 'batchReference',
							term: phrase.escape(mainBatch.reference),
							quoted: false
						};

						const quantityNode: FieldExpression = {
							field: 'qtyInvoiced',
							term: String(mainBatch.quantityValue),
							quoted: false
						};

						const notBatchIdentifierNode: OperatorExpression = {
							start: 'NOT',
							left: {
								left: batchReferenceNode,
								operator: 'AND',
								parenthesized: true,
								right: quantityNode
							}
						};

						let allNode: NodeExpression = {
							left: {
								left: supplierOrderNumberNode,
								operator: 'AND',
								parenthesized: true,
								right: {
									left: supplierOrderItemNumberNode,
									operator: 'AND',
									parenthesized: false,
									right: quantityUnitNode
								}
							},
							operator: 'AND',
							parenthesized: false,
							right: notBatchIdentifierNode
						};

						if (listOfRelatedBatches) {
							allNode = {
								left: allNode,
								operator: 'AND',
								parenthesized: false,
								right: this.createInListFilter('batchReference', listOfRelatedBatches)
							};
						}

						const mainBatchInvoiceDate = this.mainClaimBatch?.invoiceDate;
						const strictMainBatchInvoiceMoment = mainBatchInvoiceDate
							? moment(mainBatchInvoiceDate, [DATE_FORMAT_PATTERN, moment.ISO_8601], true)
							: moment();
						const parsedMainBatchInvoiceMoment = strictMainBatchInvoiceMoment.isValid()
							? strictMainBatchInvoiceMoment
							: moment(mainBatchInvoiceDate);
						const invoiceYearMoment = parsedMainBatchInvoiceMoment.isValid()
							? parsedMainBatchInvoiceMoment
							: moment();

						const invoiceDateCurrentYearNode: LuceneExpression = {
							field: 'invoiceDate',
							term_min: invoiceYearMoment.clone().startOf('year').format(DATE_FORMAT_PATTERN),
							term_max: invoiceYearMoment.clone().endOf('year').format(DATE_FORMAT_PATTERN),
							inclusive: 'both'
						};

						allNode = {
							left: allNode,
							operator: 'AND',
							parenthesized: false,
							right: invoiceDateCurrentYearNode
						};

						const transacNode = this.claimService.createTransacSoldtoAndShiptoFilter(soldTos, shipTos);

						allNode = {
							left: allNode,
							operator: 'AND',
							parenthesized: false,
							right: transacNode
						};

						const textSearch = this.searchValue;

						if (textSearch !== '') {
							const escapedTextFilter = `*${ term.escape(textSearch) }*`;

							const supplierOrderReferenceField: FieldExpression = {
								field: 'supplierOrderNumber',
								term: escapedTextFilter,
								quoted: false
							};

							const customerOrderReferenceField: FieldExpression = {
								field: 'customerOrderNumber',
								term: escapedTextFilter,
								quoted: false
							};

							const customerArticleReferenceField: FieldExpression = {
								field: 'customerArticleReference',
								term: escapedTextFilter,
								quoted: false
							};

							const steelgradeDescriptionField: FieldExpression = {
								field: 'steelgradeDescription',
								term: escapedTextFilter,
								quoted: false
							};

							const batchReferenceDescriptionField: FieldExpression = {
								field: 'batchReference',
								term: escapedTextFilter,
								quoted: false
							};

							const millOrderNumberField: FieldExpression = {
								field: 'millOrderNumber',
								term: escapedTextFilter,
								quoted: false
							};

							const heatNumberField: FieldExpression = {
								field: 'heatNumber',
								term: escapedTextFilter,
								quoted: false
							};

							const supplierCustomerNode = {
								left: supplierOrderReferenceField,
								operator: 'OR',
								right: customerOrderReferenceField,
								parenthesized: false
							};

							const articleSteelgradeNode = {
								left: customerArticleReferenceField,
								operator: 'OR',
								right: steelgradeDescriptionField,
								parenthesized: false
							};

							const intermediateNode = {
								left: supplierCustomerNode,
								operator: 'OR',
								right: articleSteelgradeNode,
								parenthesized: false
							};

							const millOrderHeatNode = {
								left: millOrderNumberField,
								operator: 'OR',
								right: heatNumberField,
								parenthesized: false
							};

							const intermediateNode2 = {
								left: batchReferenceDescriptionField,
								operator: 'OR',
								right: millOrderHeatNode,
								parenthesized: false
							};

							allNode = {
								left: allNode,
								operator: 'AND',
								parenthesized: false,
								right: {
									left: intermediateNode,
									operator: 'OR',
									right: intermediateNode2,
									parenthesized: true
								}
							};
						}

						if (!this.togglePos) {
							const batchInvoicedField: FieldExpression = {
								field: 'batchInvoiced',
								term: 'X',
								quoted: false
							};

							allNode = {
								left: allNode,
								operator: 'AND',
								right: batchInvoicedField,
								parenthesized: true
							};
						}

						return this.batchService.getBatches(
							[
								'id',
								'batchReference',
								'supplierOrderNumber',
								'supplierOrderItemNumber',
								'millOrderNumber',
								'millOrderItemNumber',
								'soldTo',
								'soldToDescription',
								'shipTo',
								'qtyInvoiced',
								'weightUnitInvoiced',
								'currencyInvoiced',
								'sfcC',
								'thicknessDimension',
								'widthDimension',
								'lengthDimension',
								'qtyBatchGross',
								'qtyBatchNet',
								'cddWeek',
								'statusDescription',
								'batchInvoiced',
								'invoiceDate'
							],
							luceneToString(allNode),
							index,
							limit,
							new OrderBy('batchReference', OrderByDirection.ASC)
						).pipe(
							map(relatedBatchList => relatedBatchList.results),
							tap(batchList => {
								if (this.count === undefined) {
									this.count = batchList.length;
								}
							})
						);
					})
				)
		);
	}

	settogglePos($event: boolean) {
		this.togglePos = $event;
	}

	createInListFilter(fieldName: string, values: string[]) {
		let filterCondition: LuceneExpression;
		if (values.length === 1) {
			filterCondition = {
				field: fieldName,
				term: values[0],
				quoted: true
			};
		} else {
			filterCondition = {
				field: fieldName,
				left: {
					field: '<implicit>',
					term: values[0],
					quoted: true
				},
				operator: '<implicit>',
				right: {
					field: '<implicit>',
					term: values[1],
					quoted: true
				},
				parenthesized: true
			};

			let current = filterCondition;
			for (const bocClientTransac of values.slice(2)) {
				current.right = {
					left: current.right,
					operator: '<implicit>',
					right: {
						field: '<implicit>',
						term: bocClientTransac,
						quoted: true
					},
					parenthesized: false
				};
				current = current.right;
			}
		}
		return filterCondition;
	}

	isChecked(batch: ClaimBatch) {
		let checkedBatch = false;
		if (this.selectedBatches.length !== 0) {
			for (const selectedBatch of this.selectedBatches) {
				if (batch.batchReference === selectedBatch?.reference) {
					checkedBatch = true;
				}
			}
		}
		return checkedBatch;
	}

	isDisabled(batch: ClaimBatch) {
		let disableBatch = false;
		if (this.selectedBatches.length === this.batchLimit && this.selectedBatches[this.batchLimit - 1] !== null) {
			disableBatch = true;
			for (const selectedBatch of this.selectedBatches) {
				if (batch.batchReference === selectedBatch?.reference) {
					disableBatch = false;
				}
			}
		}
		return disableBatch;
	}

	selectBatches(claimBatch: ClaimBatch, event: MatCheckboxChange) {
		if (event.checked) {
			for (const [index, singleBatch] of this.selectedBatches.entries()) {
				if (singleBatch === null) {
					const batch = new AllQualityIssueBatch();
					batch.reference = claimBatch.batchReference;
					batch.quantityValue = claimBatch.qtyInvoiced;
					batch.quantityUnitTr = <keyof typeof UNIT_OPTIONS>claimBatch.weightUnitInvoiced.toLowerCase();
					batch.receivedOrderSupplierItem = claimBatch.supplierOrderItemNumber;
					batch.receivedOrderSupplierReference = claimBatch.supplierOrderNumber;
					batch.receivedInvoiceQuantityUnitTr =
						<keyof typeof UNIT_OPTIONS>claimBatch.weightUnitInvoiced.toLowerCase();
					batch.receivedInvoiceAmountUnitTr =
						<keyof typeof CURRENCY_OPTIONS>claimBatch.currencyInvoiced.toLowerCase();
					batch.receivedNetQuantityValue = claimBatch.qtyBatchNet;
					batch.soldTo = claimBatch.soldTo;
					batch.sfcC = claimBatch.sfcC;
					this.selectedBatches[index] = batch;
					this.batchesFormArray.push(this.createBatchFormGroup(batch));
					if (!this.claimService.batchInvoicedOrNot(claimBatch.batchInvoiced)) {
						this.notInvoiceBatchRefArray.push(batch.reference);
					}
					break;
				}
			}
		} else {
			const dummyArr = [];
			for (const [index, singleBatch] of this.selectedBatches.entries()) {
				if (singleBatch !== null && singleBatch.reference === claimBatch.batchReference) {
					this.selectedBatches[index] = null;
					this.batchesFormArray.removeAt(index + 1);
				}
			}
			for (const singleBatch of this.selectedBatches) {
				if (singleBatch !== null) {
					dummyArr.push(singleBatch);
				}
			}
			this.selectedBatches = dummyArr;
			const arrayLength = this.batchLimit - this.selectedBatches.length;
			for (let i = 0; i < arrayLength; i++) {
				this.selectedBatches.push(null);
			}
			const delIndex = this.notInvoiceBatchRefArray.indexOf(claimBatch.batchReference);
			if (delIndex !== -1) {
				this.notInvoiceBatchRefArray.splice(delIndex, 1);
			}
		}
		this.setSelectedBatches();
		const totalBatchQty = sumBatchQuantitiesRoundValue(this.claim.batches);
		this.claim.quantityValue = totalBatchQty;
	}

	lowerCaseUnit(weightUnit: string) {
		return <keyof typeof UNIT_OPTIONS> weightUnit.toLowerCase();
	}

	setSelectedBatches() {
		this.claim.batches = [
			this.claim.batches[0],
			...this.selectedBatches.filter((batch): batch is AllQualityIssueBatch => batch !== null)
		];
	}

	initializeSelectedBatches() {
		this.selectedBatches = this.claim.batches.slice(1); // main batch excluded
		const arrayLength = this.batchLimit - this.selectedBatches.length;
		for (let i = 0; i < arrayLength; i++) {
			this.selectedBatches.push(null);
		}
	}

	quantity(batch: AllQualityIssueBatch) {
		const { value, unit } = getQualityIssueBatchQuantity(batch);
		const unitSymbol = this.unitBatchOptions[unit].symbol;
		if (!value) {
			return '';
		}
		const unitPart = ` ${ unitSymbol }`;
		return `${ formatNumber(
			value,
			'weight'
		) }${ unitPart }`;
	}

	async goToBatchSelection(event: Event) {
		event.preventDefault();
		return this.router.navigate(['../batch-selection'], { relativeTo: this.route });
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['../batch-selection'], { relativeTo: this.route });
		}
	}

	onFullQuantityCheckboxChange(index: number) {
		if (this.batchesFormArray.controls[index].controls.fullQuantity.value) {
			const batch = this.claim.batches[index];
			const { value, unit } = getQualityIssueBatchQuantity(batch);
			const newQuantity = value;
			batch.claimedQuantityValue = newQuantity;
			batch.claimedQuantityUnitTr = unit;
			this.batchesFormArray.controls[index].controls.claimedQuantity.setValue(newQuantity);
			const totalBatchQty = sumBatchQuantitiesRoundValue(this.claim.batches);
			this.claim.quantityValue = totalBatchQty;
		}
		this.updateImpactedQuantityState(index);
	}

	private updateImpactedQuantityState(index: number) {
		const formControl = this.batchesFormArray.controls[index].controls.claimedQuantity;
		if (this.claim.claimType !== 'ZR' && this.batchesFormArray.controls[index].controls.fullQuantity.value) {
			formControl.disable();
		} else {
			formControl.enable();
		}
	}

	private updateReceiptDateValidation() {
		for (const formGroup of this.batchesFormArray.controls) {
			const { receiptDate } = formGroup.controls;
			receiptDate.setValidators(
				this.claim.claimType !== 'ZR' ? [Validators.required] : []
			);
			receiptDate.updateValueAndValidity();
		}
	}

	private scrollIntoViewIfPresent(elem?: ElementRef<HTMLElement>) {
		if (elem) {
			elem.nativeElement.scrollIntoView();
		}
	}

	private evtUnrecognizedBatch(unrecognizedBatchReference: string) {
		const sub = this.dialog.open<DialogUnrecognizedBatchComponent, number>(
			DialogUnrecognizedBatchComponent,
			{ disableClose: true }
		);

		sub.componentInstance.unrecognizedBatch = unrecognizedBatchReference;
		this.unrecognizedBatchSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM) {
					await this.router.navigate(['../batch-selection'], { relativeTo: this.route });
				}
			});
	}

	private constructInvoiceDocumentFilter(
		supplierOrderReference: string,
		supplierOrderReferenceItem: string,
		batchReference: string,
	) {
		const supplierOrderReferenceItemField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReferenceItem`,
			term: supplierOrderReferenceItem,
			quoted: true
		};
		const batchNumberField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.batchNumber`,
			term: batchReference,
			quoted: true
		};
		const supplierOrderReferenceFieldFromDocLine: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReference`,
			term: padSupplierOrderReference(supplierOrderReference),
			quoted: true
		};
		const supplierOrderNumberNode: NodeExpression = {
			left: supplierOrderReferenceFieldFromDocLine,
			operator: 'AND',
			right: supplierOrderReferenceItemField,
			parenthesized: true
		};
		const batchNumberNode = {
			left: supplierOrderNumberNode,
			operator: 'AND',
			right: batchNumberField,
			parenthesized: true
		};
		const invoiceNode = {
			left: batchNumberNode,
			operator: 'AND',
			right: {
				left: {
					field: 'type',
					term: 'IN',
					quoted: true
				},
				operator: 'AND',
				right: {
					field: 'subtype',
					term: 'FACTU',
					quoted: true
				},
				parenthesized: false
			},
			parenthesized: false
		};
		return luceneToString(invoiceNode);
	}
}
